import { defineStore } from 'pinia'
import Cookies from 'js-cookie'
import * as Countries from 'countries-list'
import dayjs from '~/utils/dayjs'
import { formatWithIntl } from '~/utils/currency'

import { usePropertyStore } from './property'
import { useRuntimeConfig } from '#app'

const ATTRIBUTION_COOKIE_TO_HEADER: Record<string, string> = {
	_tripening_gclid: 'X-TRIPENING-GCLID',
	_tripening_gclsrc: 'X-TRIPENING-GCLSRC',
	_tripening_utm_source: 'X-TRIPENING-UTM-SOURCE',
	_tripening_utm_medium: 'X-TRIPENING-UTM-MEDIUM',
	_tripening_utm_campaign: 'X-TRIPENING-UTM-CAMPAIGN',
	_tripening_utm_term: 'X-TRIPENING-UTM-TERM',
	_tripening_utm_content: 'X-TRIPENING-UTM-CONTENT',
	// Signed PPC campaign token (ptok) — ETL margin/channel rules; order-api
	// persists it so checkout re-pricing matches the landing price.
	_tripening_ppc: 'X-PPC-Campaign',
}

// Query params the landing URL carries, mapped to the cookie they are stored as.
// The property page prices off `route.query.ptok || cookie`, so a missing cookie
// still shows the campaign price — but add-to-cart read the cookie ONLY, and the
// cart was then priced at the default margin. Result: landing quoted net x 1.25,
// checkout charged x 1.15 (prod order 11567007, 2026-08-23). The cookie can be
// absent for ordinary reasons — consent not yet granted, ITP, a plugin that has
// not run yet — so the URL has to be a fallback, exactly as the landing treats it.
const ATTRIBUTION_QUERY_TO_COOKIE: Record<string, string> = {
	gclid: '_tripening_gclid',
	gclsrc: '_tripening_gclsrc',
	utm_source: '_tripening_utm_source',
	utm_medium: '_tripening_utm_medium',
	utm_campaign: '_tripening_utm_campaign',
	utm_term: '_tripening_utm_term',
	utm_content: '_tripening_utm_content',
	ptok: '_tripening_ppc',
}

function getAttributionQueryValues(): Record<string, string> {
	// Client-only: on the server there is no window and the header set simply
	// falls back to cookies.
	if (typeof window === 'undefined') return {}

	const out: Record<string, string> = {}
	try {
		const params = new URLSearchParams(window.location.search)
		for (const [param, cookieName] of Object.entries(ATTRIBUTION_QUERY_TO_COOKIE)) {
			const value = params.get(param)
			if (value) out[cookieName] = value
		}
	} catch {
		// Malformed URL — cookies still apply.
	}
	return out
}

function getAttributionHeaders() {
	const headers: Record<string, string> = {}
	const fromQuery = getAttributionQueryValues()

	for (const [cookieName, headerName] of Object.entries(ATTRIBUTION_COOKIE_TO_HEADER)) {
		// Cookie wins: it is the durable record of the click that started the
		// session. The URL is only a fallback for the landing hit itself.
		const value = Cookies.get(cookieName) || fromQuery[cookieName]
		if (value) {
			headers[headerName] = value
		}
	}

	return headers
}

export function getJsonHeaders(extraHeaders: Record<string, string> = {}) {
	return {
		'Content-Type': 'application/json',
		'Accept': 'application/json',
		...getAttributionHeaders(),
		...extraHeaders,
	}
}

function getCountryOption(countryCode?: string | null) {
	if (!countryCode) {
		return null
	}

	const countryName = (Countries.countries as Record<string, { name: string }>)[countryCode]?.name || countryCode

	return {
		value: countryCode,
		label: countryName,
	}
}

function getStateOption(stateCode?: string | null) {
	if (!stateCode) {
		return null
	}

	return {
		value: stateCode,
		label: stateCode,
	}
}

function normalizeIdentityValue(value: unknown) {
	return String(value ?? '').trim().toLowerCase()
}

function getPaxIdentityValue(pax: Record<string, any>, keys: string[]) {
	const normalizedPax = Object.keys(pax || {}).reduce<Record<string, any>>((acc, key) => {
		acc[key.toLowerCase()] = pax[key]
		return acc
	}, {})

	for (const key of keys) {
		const value = normalizedPax[key]
		if (value !== undefined && value !== null && value !== '') {
			return value
		}
	}

	return ''
}

function paxDiffersFromCustomerInfo(pax: Record<string, any>, customerInfo: Record<string, any>) {
	if (!pax || !Object.keys(pax).length) {
		return false
	}

	const paxIdentity = {
		firstName: normalizeIdentityValue(
			getPaxIdentityValue(pax, ['first_name', 'firstname', 'first name', 'first-name', 'name'])
		),
		lastName: normalizeIdentityValue(
			getPaxIdentityValue(pax, ['last_name', 'lastname', 'last name', 'last-name', 'surname', 'family_name'])
		),
		email: normalizeIdentityValue(getPaxIdentityValue(pax, ['email', 'e-mail'])),
		phone: normalizeIdentityValue(getPaxIdentityValue(pax, ['phone', 'phone_number', 'phonenumber', 'mobile'])),
	}

	const customerIdentity = {
		firstName: normalizeIdentityValue(customerInfo?.first_name),
		lastName: normalizeIdentityValue(customerInfo?.last_name),
		email: normalizeIdentityValue(customerInfo?.email),
		phone: normalizeIdentityValue(customerInfo?.phone),
	}

	const populatedFields = Object.entries(paxIdentity).filter(([, value]) => value)
	if (!populatedFields.length) {
		return false
	}

	return populatedFields.some(([field, value]) => {
		return value !== customerIdentity[field as keyof typeof customerIdentity]
	})
}

// Order states in which order-api has not prepared payment yet.
//
// `confirm-pax-info` is what moves an order price_checked -> ready_to_payment, and it is the
// only call that prebooks the supplier and creates the GAR DirectPayment row. Until it runs, a
// prepaid GAR express order legitimately reads deposit > 0 with pay_now_total = 0 and
// check_in_amount = 0: order-api computes pay_now_total as direct_payments_total +
// chargeable_amount, a GAR card_booking rate is never chargeable by us (GetARoom is merchant of
// record), and there is no DirectPayment yet. Reading that as "broken totals" fired the guard on
// essentially every hotel checkout from the moment /checkout opened.
const PRE_PAYMENT_ORDER_STATES = ['new', 'price_checked']

export const useBookingStore = defineStore('checkout', {
	state: () => {
		const config = useRuntimeConfig();
		return {
			order: {} as any,
			formattedFields: {
				checkInDate: '',
				checkInTime: '',
				checkOutDate: '',
				checkOutTime: '',
				nightsCount: 0,
				nightsCountLabel: 0,
				pricePerNight: 0,
				totalPrice: 0,
        totalPriceCurrency: 'USD'
			} as any,
			product: {} as any,
			rate: {} as any,
			rates: [] as any,
			customerSchema: {} as any,
			contactInfo: {} as any,
			leadGuest: {} as any,
			customerRemarks: '' as string,
			billingForm: {
				address: '',
				city: '',
				country: null,
				state: null,
				zip: '',
			} as any,
			checkoutDialog: {
				visible: false,
				loading: false,
				failed: false,
				failedPayment: false,
        failedBooking: false,
				failed3Ds: false
			} as any,
			agreeWithTerms: true,
			direct_payments: [] as any,
			publicOrder: {} as any,
      productType: <string>'',
      travelersInfo: [],
      bookingAnswers: {} as any,
			host: config.public.baseURL,
      userOrders: [],
      roomContext: {
        roomName: '',
        roomImages: [] as any[]
      }
		}
	},
	getters: {
		getBillingForm: (state) => state.billingForm,
		hasInvalidHotelCheckoutTotals: (state) => {
			const order = state.order || {}
			const itemType = state.productType || order?.items?.[0]?.type
			if (itemType !== 'hotel') {
				return false
			}

			// No order loaded yet, or payment has not been prepared yet (see
			// PRE_PAYMENT_ORDER_STATES): zero totals are the expected reading, not a fault.
			// Only an order that is past confirm-pax-info and STILL has nothing collectable
			// anywhere is genuinely unpayable.
			const orderState = String(order?.state ?? '')
			if (!orderState || PRE_PAYMENT_ORDER_STATES.includes(orderState)) {
				return false
			}

			const depositAmount = Number(order?.deposit?.amount ?? 0)
			const payNowAmount = Number(order?.pay_now_total?.amount ?? 0)
			const checkInAmount = Number(order?.check_in_amount?.amount ?? 0)

			return depositAmount > 0 && payNowAmount <= 0 && checkInAmount <= 0
		},
	},
	actions: {
		hydrateCheckoutState(value: any) {
			const customerInfo = value?.customer_info || {}
			const billingInfo = value?.billing_info || {}
			const firstItem = value?.items?.[0]

			// Only overwrite contactInfo/billingForm from server when the server
			// actually has data, OR the store is still empty.  This prevents
			// mid-flow setOrder calls (e.g. after confirm-pax-info, which does
			// NOT save customer/billing info) from wiping locally-entered data.
			const hasServerContact = Object.values(customerInfo).some(v => v)
			const hasLocalContact = Object.values(this.contactInfo || {}).some(v => v)
			if (hasServerContact || !hasLocalContact) {
				this.contactInfo = { ...customerInfo }
			}

			const hasServerBilling = !!(billingInfo.address_line_1 || billingInfo.city || billingInfo.country_code || billingInfo.zip_code)
			const hasLocalBilling = !!(this.billingForm?.address || this.billingForm?.city || this.billingForm?.zip)
			if (hasServerBilling || !hasLocalBilling) {
				this.billingForm = {
					address: billingInfo.address_line_1 || '',
					city: billingInfo.city || '',
					country: getCountryOption(billingInfo.country_code),
					state: getStateOption(billingInfo.state_code),
					zip: billingInfo.zip_code || '',
				}
			}

			if (!firstItem) {
				this.leadGuest = {}
				this.travelersInfo = []
				this.bookingAnswers = {}
				this.customerRemarks = ''
				return
			}

			if (firstItem.type === 'hotel') {
				const firstRate = firstItem.room_rates?.[0]
				const firstPax = firstRate?.paxes?.find((pax: any) => pax && Object.keys(pax).length)

				this.leadGuest =
					firstPax && paxDiffersFromCustomerInfo(firstPax, customerInfo) ? { ...firstPax } : {}
				this.travelersInfo = []
				this.bookingAnswers = {}
				this.customerRemarks = firstRate?.customer_remarks || ''
				return
			}

			this.leadGuest = {}
			this.travelersInfo = (firstItem.availabilities || [])
				.filter((availability: any) => availability?.uid && availability?.pax && Object.keys(availability.pax).length)
				.map((availability: any) => ({
					uid: availability.uid,
					pax: { ...availability.pax },
				}))
			this.bookingAnswers = { ...(firstItem.booking_answers || {}) }
			this.customerRemarks = ''
		},
		setOrder(value: any) {
			this.order = value
			if (value.items?.[0]) {
				this.product = value.items[0]
        this.productType = this.product.type
        if (this.product.type === 'hotel') {
          this.rate = this.product.room_rates?.[0] || {}
          this.rates = this.product.room_rates || []
          this.setFormattedFields(value)
        }
			} else {
				this.product = {}
				this.productType = ''
				this.rate = {}
				this.rates = []
			}
			this.hydrateCheckoutState(value)
		},
    setUserOrders(value: any) {
      this.userOrders = value
    },
		setFormattedFields(value: any) {
			const product = value.items?.[0]
			if (product) {
				const startDate = dayjs(product.criteria.date_start)
				const endDate = dayjs(product.criteria.date_end)
				this.formattedFields.checkInDate = startDate.format('ddd, DD MMM')
				this.formattedFields.checkOutDate = endDate.format('ddd, DD MMM')
				this.formattedFields.nightsCount = endDate.diff(startDate, 'days')
				this.formattedFields.nightsCountLabel = `${this.formattedFields.nightsCount} night${this.formattedFields.nightsCount === 1 ? '' : 's'}`

				// Derive amounts from the currency CODE, not formatted[0] — multi-char symbols like
				// "S$" (SGD) would otherwise slice to "S" and drop the "$".
				const currencyCode = value.subtotal_without_taxes?.currency || value.pay_now_total?.currency || 'USD'
				const subtotal = Number(value.subtotal_without_taxes.amount)
				const nights = this.formattedFields.nightsCount
				const isPerPerson = this.rate?.sell_mode === 'per_person'
				const occupancy = isPerPerson ? (this.rate?.occupancy || Number(product.criteria?.adults) || 1) : 1
				const divisor = nights * occupancy
				this.formattedFields.pricePerNight = formatWithIntl(subtotal / divisor, currencyCode)
				this.formattedFields.adultsCount = occupancy
				this.formattedFields.totalPrice = formatWithIntl(Number(value.check_in_amount.amount) + Number(value.pay_now_total.amount), currencyCode)
        this.formattedFields.totalPriceCurrency = value.pay_now_total.currency
			}
		},
		getCart() {
			const api = useApi()
			return api(`${this.host}/api/order/v3/cart`, {
				method: 'GET',
				parseResponse: JSON.parse,
        headers: getJsonHeaders(),
			})
		},
		async createExpressOrder(payload: any) {
			const api = useApi()
			try {
				const expressHeaders = getJsonHeaders()
				const wref = Cookies.get('_tripening_wref')
				if (wref) {
					expressHeaders['X-TRIPENING-WIDGET-UID'] = wref
				}
				const order: any = await api(`${this.host}/api/order/v3/orders/create-express-order`, {
					method: 'POST',
					body: JSON.stringify(payload),
					parseResponse: JSON.parse,
					headers: expressHeaders,
				})
				this.getCustomerSchema(order.uid)
				// this.setOrder(order)
				return order
			} catch(error: any) {
			const propertyStore = usePropertyStore()

			// Handle both error structures: error.data.errors (ofetch) and error.response._data.errors (legacy)
			const _errors = error.data?.errors || error.response?._data?.errors
			const SOLD_OUT_CODE = 2315
			const _soldOutError = _errors?.find((e: any) => e.code === SOLD_OUT_CODE)
			if (_soldOutError) {
				propertyStore.addSoldOutRate(payload.room_rates[0].ref)
				propertyStore.setCurrentSoldOutRate(payload.room_rates[0].ref)
			}
			throw error // Re-throw so caller knows it failed
		}
		},
		async getOrderById(id: string) {
			const api = useApi()
			const order: any = await api(`${this.host}/api/order/v3/orders/${id}`, {
				method: 'GET',
				headers: getJsonHeaders(),
			})
			const _orderId = order.uid
			this.setOrder(order);
			if (order.state !== 'failed') {
				await this.getCustomerSchema(_orderId)
			}
			return order
		},
    async changeOrderState(uid: string, state: string) {
      const api = useApi()
      await api(`${this.host}/api/order/v3/orders/${uid}/change-state`, {
        method: 'POST',
        body: JSON.stringify({state}),
        headers: getJsonHeaders(),
      })

      if (this.order?.uid === uid) {
        this.order = {
          ...this.order,
          state,
        }
      }
    },
		clearOrder() {
			this.order = {}
		},
		setCustomerSchema(value: any) {
			this.customerSchema = value
		},
		setContactInfo(value: any) {
			this.contactInfo = value
		},
		setLeadGuestInfo(value: any) {
			this.leadGuest = value
		},
    setTravelersInfo(value: any) {
      this.travelersInfo = value
    },
    setBookingAnswers(value: any) {
      this.bookingAnswers = value
    },
		setCustomerRemarks(value: string) {
			this.customerRemarks = value
		},
		async updatePaxInfo(orderId: string, payload: any) {
			const api = useApi()

			const response = await api(`${this.host}/api/order/v3/orders/${orderId}/confirm-pax-info`, {
				method: 'POST',
				body: JSON.stringify(payload),
				parseResponse: JSON.parse,
        headers: getJsonHeaders(),
			})
      this.setOrder(response);
			//TODO: maybe this can be the way to update contact info
			//await this.updateContactInfo(orderId)
			return response
		},
		async getCustomerSchema(orderId: string) {
			const api = useApi()
			const schema = await api(`${this.host}/api/order/v3/orders/${orderId}/customer-schema`, {
				method: 'GET',
				headers: getJsonHeaders(),
			})
			this.setCustomerSchema(schema)
			return schema
		},
		async updateContactInfo(orderId: string, payload: any) {
			const _data = {
				customer_info: this.contactInfo,
				billing_info: {}
			}

			const api = useApi()
			const response = await api(`${this.host}/api/order/v2/orders/${orderId}/update-billing-info`, {
				method: 'POST',
				body: JSON.stringify(_data),
				parseResponse: JSON.parse,
				headers: getJsonHeaders(),
			})
		},
		async updateBillingInfo(orderId: string, payload: any) {
			const convertedBillingObject = {
				address_line_1: payload.address,
				city: payload.city,
				country_code: payload.country.value,
				state_code: payload.state.value,
				zip_code: payload.zip,
			}

			const _data = {
				customer_info: this.contactInfo,
				billing_info: convertedBillingObject || {}
			}

			const api = useApi()
			const response = await api(`${this.host}/api/order/v2/orders/${orderId}/update-billing-info`, {
				method: 'POST',
				body: JSON.stringify(_data),
				parseResponse: JSON.parse,
				headers: getJsonHeaders(),
			})

			return response
		},
    async getPublicOrderInfo(id: string) {
      const api = useApi()
      const order = await api(`${this.host}/api/order/v3/orders/${id}`, {
        method: 'GET',
        headers: getJsonHeaders(),
      })
      this.publicOrder = order;
      return order
    },
    async addItemToCart(payload: any) {
      const api = useApi()
      const response = await api(`${this.host}/api/order/v3/cart/items`, {
        method: 'POST',
        body: JSON.stringify(payload),
        parseResponse: JSON.parse,
        headers: getJsonHeaders(),
      })
      return response
    },
    async createActivityExpressOrder(payload: any) {
      const api = useApi()
      try {
        const activityHeaders = getJsonHeaders()
        const actWref = Cookies.get('_tripening_wref')
        if (actWref) {
          activityHeaders['X-TRIPENING-WIDGET-UID'] = actWref
        }
        const order = await api(`${this.host}/api/order/v3/orders/create-express-order`, {
          method: 'POST',
          body: JSON.stringify(payload),
          parseResponse: JSON.parse,
          headers: activityHeaders,
        })
        return order
      } catch(error) {
        // Rethrow. Swallowing here returned undefined to the caller, which then blew up
        // destructuring `{ uid }` — so a failed booking surfaced as an unrelated TypeError
        // and the user saw the button silently re-enable with no explanation.
        console.error('[activity] create express order failed', error)
        throw error
      }
    },
    async getUserOrders() {
      const api = useApi()
      const { orders } = await api(`${this.host}/api/order/v3/orders`, {
        method: 'GET',
        headers: getJsonHeaders(),
      })
      this.setUserOrders(orders);
      return orders
    },
    setRoomContext(roomName: string, roomImages: any[]) {
      this.roomContext = { roomName, roomImages }
      if (typeof window !== 'undefined') {
        localStorage.setItem('roomContext', JSON.stringify(this.roomContext))
      }
    },
    loadRoomContext() {
      if (typeof window !== 'undefined') {
        const stored = localStorage.getItem('roomContext')
        if (stored) {
          try {
            this.roomContext = JSON.parse(stored)
          } catch (e) {
            this.roomContext = { roomName: '', roomImages: [] }
          }
        }
      }
    },
    clearRoomContext() {
      this.roomContext = { roomName: '', roomImages: [] }
      if (typeof window !== 'undefined') {
        localStorage.removeItem('roomContext')
      }
    },
	},
})
